import React, { useCallback, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { getDateValueInTimeZone } from "Components/Generic/MilestoneDates";
import { useTranslation } from "react-i18next";
import { PiWarning } from "react-icons/pi";
import { useHistory } from "react-router-dom";
import { getStatusBadgeColorsByAvailability } from "Util/fleetStatus";
import { getHoursDiffering } from "Util/formatDateAndTime";
import { handlesGrid } from "Util/handlesGrid";

import ConfirmationModal from "../../../../../../../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../../../../../../../components/Form/Button";
import BadgeStatus from "../../../../../../../../../../components/Generic/BadgeStatus";
import IconCloseCircle from "../../../../../../../../../../components/Generic/Icons/CloseCircle";
import { NotificationToast } from "../../../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../../../context/StateContext";
import {
  MemberAvailability,
  MemberAvailabilityAllDayAbsence,
  MemberAvailabilityPartialAbsence,
} from "../../../../../../../../../../models/members/member.availability";
import { Filters, Searches } from "../../../../../../../../../../models/shipeezi.table";
import AvailabilityService from "../../../../../../../../../../services/availability.service";
import {
  MemberAvailabilityStatus,
  MemberAvailabilityType,
} from "../../../../../../../../../../util/enums/members";
import { AvailabilityKeys } from "../../../../../../../../../../util/queries";
import { useActions } from "../../../hooks/actions";
import { NewRecordButton } from "../../NewRecordButton";
import ManageAvailability, { ManageAvailabilityHandles } from "./ManageAvailability";
import { useParams } from "react-router-dom";
import {
  ClickableRowActionButton,
  ColumnDateShow,
  ColumnStatus,
  ConfirmationModalBody,
  ConfirmationModalBodyText,
  useStyles,
} from "./styles";

type Props = {};

export const AvailabilityTab: React.FC<Props> = () => {
  const {
    member: { fullName },
  } = useActions();

  const { memberId } = useParams<{ memberId: string }>();

  const { t: translate } = useTranslation();
  const { location } = useHistory<{ isView?: boolean }>();
  const { currentUser, userTimezone } = useAppState();
  const queryClient = useQueryClient();
  const classes = useStyles();

  const manageAvaialbilityRef = useRef<ManageAvailabilityHandles>(null);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [availabiltyToCancel, setAvailabilityToCancel] = useState<{ id: number } | null>();

  const { handleCurrentPage, handlePageSize } = handlesGrid({
    setPage,
    setPageSize,
  });

  const {
    refetch,
    data: availabilities = [],
    isFetching,
  } = useQuery({
    queryKey: AvailabilityKeys.getAll([page, pageSize, filters, searches]),
    queryFn: async () => {
      const response = await AvailabilityService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
        include: JSON.stringify([
          {
            association: "members",
            where: {
              id: Number(memberId),
            },
            attributes: ["id", "firstName", "lastName", "fullName"],
          },
        ]),
        ...filters,
      });

      if (response?.data && Array.isArray(response.data)) return response.data;

      return [];
    },
    enabled: !!Number(memberId),
    refetchOnWindowFocus: false,
  });

  const { data: availabilitiesTotal, refetch: refetchTotal } = useQuery({
    queryKey: AvailabilityKeys.count([page, pageSize, filters]),
    queryFn: async () => {
      const response = await AvailabilityService.count({
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          {
            association: "members",
            where: {
              id: Number(memberId),
            },
          },
        ]),
        distinctCount: true,
        ...filters,
      });

      if (response?.data) return response.data;

      return 0;
    },
  });

  const { mutate: createAvailability, isLoading: isLoadingCreateAvailability } = useMutation({
    mutationKey: AvailabilityKeys.create(),
    mutationFn: async ({ data }: { data: MemberAvailability }) =>
      AvailabilityService.create({
        ...data,
        memberId: Number(memberId),
      }),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      queryClient.invalidateQueries({ queryKey: [AvailabilityKeys.getAll] });
      setOpenConfirmationModal(false);
      setAvailabilityToCancel(null);
      manageAvaialbilityRef.current?.close();
      refetch();
      refetchTotal();
    },
    onError: (error) => {
      console.error(error);
      NotificationToast.error(translate("anErrorOccurred"));
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationKey: AvailabilityKeys.update(),
    mutationFn: async ({ id }: { id: number }) => AvailabilityService.cancelAvailabilty(id),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      queryClient.invalidateQueries({ queryKey: [AvailabilityKeys.getAll] });
      setOpenConfirmationModal(false);
      setAvailabilityToCancel(null);
      refetch();
    },
    onError: (error) => {
      console.error(error);
      NotificationToast.error(translate("anErrorOccurred"));
    },
  });

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const onCancelAvailabilityRow = (data: MemberAvailability) => {
    if (data?.id) {
      setAvailabilityToCancel({ id: data?.id });
      setOpenConfirmationModal(true);
    }
  };

  const onAddAvailability = (
    data: MemberAvailabilityAllDayAbsence | MemberAvailabilityPartialAbsence
  ) => {
    createAvailability({ data });
  };

  const onOpenNewAvailabilityPopup = useCallback(() => manageAvaialbilityRef.current?.open(), []);

  const renderDatetime = (date: string | undefined, showTime: boolean) => {
    const dateWithTimeZone = getDateValueInTimeZone(date, "dd/MM/yyyy", userTimezone?.name);
    const timeWithTimeZone = getDateValueInTimeZone(date, "HH:mm", userTimezone?.name);

    if (dateWithTimeZone) {
      return (
        <ColumnDateShow>
          <span>{dateWithTimeZone}</span>
          {showTime && !!timeWithTimeZone && <span>{timeWithTimeZone}</span>}
        </ColumnDateShow>
      );
    }

    return "-";
  };

  const columns: Column[] = [
    {
      name: "type",
      title: translate("type"),
    },
    {
      name: "dateFrom",
      title: translate("from"),
      hideSearch: true,
      formatComponent: (_, row?: MemberAvailability) => {
        const date = row?.dateFrom;

        return renderDatetime(date, !row?.allDayAbsence);
      },
    },
    {
      name: "dateTo",
      title: translate("to"),
      hideSearch: true,
      formatComponent: (_, row?: MemberAvailability) => {
        const date = row?.dateTo || row?.dateFrom;

        return renderDatetime(date, !row?.allDayAbsence);
      },
    },
    {
      name: "hours",
      title: translate("hours"),
      hideSearch: true,
      formatComponent: (_, row?: MemberAvailability) => {
        if (row?.allDayAbsence) {
          return "-";
        }

        const startDate = row?.dateFrom ? new Date(row.dateFrom) : null;
        const endDate = row?.dateTo ? new Date(row.dateTo) : null;

        if (startDate && endDate) {
          startDate.setHours(startDate.getHours());
          startDate.setMinutes(startDate.getMinutes());

          endDate.setHours(endDate.getHours());
          endDate.setMinutes(endDate.getMinutes());

          return <span>{getHoursDiffering(startDate, endDate)}</span>;
        }

        return "-";
      },
    },
    {
      name: "members",
      hideSearch: true,
      title: translate("createdBy"),
      formatText: (member: MemberAvailability["members"]) => member?.[0]?.fullName ?? "-",
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (_, row?: MemberAvailability) => {
        if (!row) return "-";

        const { label, background, color } = getStatusBadgeColorsByAvailability(row, translate);

        return (
          <ColumnStatus>
            <BadgeStatus
              value={label}
              useExternalColor
              backgroundColor={background}
              textColor={color}
            />
          </ColumnStatus>
        );
      },
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "newRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          column: "type",
          name: translate("type"),
          options: Object.values(MemberAvailabilityType).map((option) => ({
            label: option,
            value: option,
          })),
        },
      ],
      onChange: handleFilter,
    },
    newRecord: location?.state?.isView ? undefined : (
      <NewRecordButton label={translate("newAbsence")} onClick={onOpenNewAvailabilityPopup} />
    ),
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: "Cancel",
      buttonAndAction(rowId, row: MemberAvailability) {
        if (
          row?.status?.toLowerCase() === MemberAvailabilityStatus.CANCELLED.toLowerCase() ||
          row?.status?.toLowerCase() === MemberAvailabilityStatus.COMPLETED.toLowerCase()
        ) {
          return null;
        }

        if (!row?.id) return null;

        return (
          <Tooltip title={translate("cancel")}>
            <ClickableRowActionButton disabled={location?.state?.isView}>
              <div
                onClick={() => (location?.state?.isView ? undefined : onCancelAvailabilityRow(row))}
              >
                <IconCloseCircle height={20} width={20} color={colors.error500} />
              </div>
            </ClickableRowActionButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <ShipeeziNewGrid
        data={availabilities}
        totalItems={Number(availabilitiesTotal)}
        columns={columns}
        showSelection={false}
        showSort={false}
        currentPage={page}
        pageSize={pageSize}
        pageSizeList={[10, 20, 40, 60]}
        onChangePageSize={handlePageSize}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        showFooterGrid
        positionActionsColumn="right"
        generalActions={generalActions}
        keyExtractor={(data) => data?.id?.toString()}
        rowActionsExtra={rowActionsExtra}
        loadingData={isFetching}
        emptyData={translate("noInformationToBeShown")}
      />

      <ManageAvailability
        ref={manageAvaialbilityRef}
        onAddNewAvailability={onAddAvailability}
        isLoading={isLoadingCreateAvailability}
      />

      <ConfirmationModal
        open={openConfirmationModal}
        title={translate("cancelAvailability")}
        closeModal={() => setOpenConfirmationModal(false)}
        leftIconTitle={<PiWarning color={colors.error600} />}
        loading={isLoading}
        submit={() => availabiltyToCancel && mutate(availabiltyToCancel)}
        textSubmit={translate("yesCancel")}
        textCancel={translate("noKeepIt")}
        customColorCancel={Color.TEXT_INFO}
        customColorSave={Color.ERROR}
        type="error"
        maxWidth="520px"
        modalFooterCustomClass={classes.modalFooter}
        showCancelButton
      >
        <ConfirmationModalBody>
          <ConfirmationModalBodyText>
            {translate("doYouReallyWantToCancelThisAbsence")} {`${fullName}?`}
          </ConfirmationModalBodyText>
          <ConfirmationModalBodyText isError>
            {translate("thisActionCannotBeUndone")}
          </ConfirmationModalBodyText>
        </ConfirmationModalBody>
      </ConfirmationModal>
    </>
  );
};
